import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {Button, Chip, FormControl, InputLabel, MenuItem, Select, TextField, Typography} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import * as Yup from "yup";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {config} from "../../../constant";

export default function DialogEditArchive({open, onClose, archiveId, archiveCategories, archivePeople}) {
    const [fetched, setFetched] = useState(false);
    const [nbAwardsByPerson, setNbAwardsByPerson] = useState(null);
    const [maxNbAwards, setMaxNbAwards] = useState(0);
    const navigate = useNavigate();

    const validationSchema = Yup.object().shape({
        year: Yup.number("Dernière année doit être numérique").transform(value => (Number.isNaN(value) ? undefined : value)).required('Année est obligatoire'),
        categoriesIds: Yup.array().min(1, 'Catégories sont obligatoires'),
        archiveAwards: Yup.array().of(Yup.object().shape({
                winnersIds: Yup.array().min(1, 'Vainqueurs sont obligatoires')}))
    });

    const {register, reset, setValue, control, handleSubmit, getValues, formState: {errors}} = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            year: null,
            categoriesIds: [],
            archiveAwards: [],
            losersIds: [],
            midReport: '',
            report: ''
        }
    });

    const {fields, replace} = useFieldArray({
        control,
        name: "archiveAwards"
    });

    useEffect(() => {
        if (archiveId) {
            fetch(`${config.BASE_URL}/archives/${archiveId}`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`${response.status} ${response.statusText}`)
                    }
                    return response.json();
                }).then(data => {
                    reset({
                        year: data.year,
                        categoriesIds: data.archiveCategories.map(archiveCategory => archiveCategory.id),
                        archiveAwards: data.archiveAwards.map(archiveAward => archiveAwardsBeanToJson(archiveAward)),
                        losersIds: data.archiveRanks[0].people.map(person => person.id),
                        midReport: data.midReport,
                        report: data.report
                    });
                    updateNbAwardsByPerson(data);
                    setFetched(true);
                }).catch((error) => {
                    const message = 'Erreur dans la récupération de l\'archive';
                    console.error(message, error);
                    navigate("/error", {state: {message: message}});
                });
        }
    }, []);

    const saveArchive = (archive) => {
        const requestOptions = {
            method: archiveId ? 'PUT' : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(archive)
        };
        fetch(`${config.BASE_URL}/archives`, requestOptions)
            .then(response => {
                console.log(response.status);
                onClose(true);
            })
            .catch((error) => {
                const message = 'Erreur dans la sauvegarde de l\'archive';
                console.error(message, error);
                navigate("/error", {state: {message: message}});
            });
    };

    const updateNbAwardsByPerson = (archive) => {
        const newNbAwardsByPerson = new Map();
        let max = 0;
        archive.archiveRanks.forEach(archiveRank => {
            if (archiveRank.nbAwards > max) {
                max = archiveRank.nbAwards;
            }
            archiveRank.people.forEach(person => {
                newNbAwardsByPerson.set(person.id, archiveRank.nbAwards);
            });
        });
        setNbAwardsByPerson(newNbAwardsByPerson);
        setMaxNbAwards(max);
    };

    const updateNbAwardsByPersonFromAwards = (archiveAwards) => {
        const newNbAwardsByPerson = new Map();
        let max = 0;
        archiveAwards.forEach(archiveAward => {
            archiveAward.winnersIds.forEach(winnerId => {
                let nbAwards = newNbAwardsByPerson.get(winnerId);
                if (nbAwards) {
                    nbAwards += 1;
                } else {
                    nbAwards = 1;
                }
                newNbAwardsByPerson.set(winnerId, nbAwards);
                if (nbAwards > max) {
                    max = nbAwards;
                }
            });
        });
        setNbAwardsByPerson(newNbAwardsByPerson);
        setMaxNbAwards(max);
    };

    const getArchiveCategory = (id) => archiveCategories.find(archiveCategory => archiveCategory.id === id);

    const handleCategoriesChange = (event) => {
        const oldArchiveAwards = getValues().archiveAwards;

        // Il faut créer une nouvelle liste archiveAwards à partir des ID de catégories sélextionnés
        const newArchiveAwards = event.target.value.map(categoryId => {
            const oldArchiveAward = oldArchiveAwards.find(current => current.categoryId === categoryId);
            if (oldArchiveAward) {
                return oldArchiveAward;
            }
            const archiveCategory = getArchiveCategory(categoryId);
            return {
                categoryId: categoryId,
                categoryName: archiveCategory.name,
                reason: '',
                winnersIds: []
            }
        });

        replace(newArchiveAwards);
        updateNbAwardsByPersonFromAwards(newArchiveAwards);
    };

    const getArchivePerson = (id) => archivePeople.find(archivePerson => archivePerson.id === id);

    const handleWinnersChange = (event) => {
        updateNbAwardsByPersonFromAwards(getValues().archiveAwards);
    };

    const archiveAwardsJsonToBean = (archiveAwardsJson) => {
        return {
            reason: archiveAwardsJson.reason,
            archiveCategory: getArchiveCategory(archiveAwardsJson.categoryId),
            winners: archiveAwardsJson.winnersIds.map(winnerId => getArchivePerson(winnerId))
        }
    };

    const archiveAwardsBeanToJson = (archiveAwardsJson) => {
        return {
            categoryId: archiveAwardsJson.archiveCategory.id,
            categoryName: archiveAwardsJson.archiveCategory.name,
            reason: archiveAwardsJson.reason,
            winnersIds: archiveAwardsJson.winners.map(winner => winner.id)
        }
    };

    const onSubmit = (data) => {
        const newArchive = {
            id: archiveId,
            year: data.year,
            archiveRanks: [{
                position: 1,
                people: data.losersIds.map(loserId => getArchivePerson(loserId))
            }],
            report: data.report,
            midReport: data.midReport,
            archiveCategories: data.categoriesIds.map(categoryId => getArchiveCategory(categoryId)),
            archiveAwards: data.archiveAwards.map(archiveAward => archiveAwardsJsonToBean(archiveAward))
        };

        saveArchive(newArchive);
        onClose(true);
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth={'md'} fullWidth>
            <DialogTitle>Archive</DialogTitle>
            <DialogContent>
                {(!archiveId || fetched) && (
                    <form id="archivePersonForm" onSubmit={handleSubmit(onSubmit)}>
                        {/*Année*/}
                        <TextField
                            type="number"
                            margin="dense"
                            id="year"
                            name="year"
                            label="Année"
                            fullWidth
                            variant="standard"
                            {...register("year")}
                            error={!!errors.year}
                        />
                        <Typography variant="inherit" color="textSecondary">{errors.year?.message}</Typography>

                        {/*Catégories*/}
                        <FormControl sx={{mt: 1}} fullWidth>
                            <InputLabel id="categories-label" error={!!errors.categoriesIds}>Catégories</InputLabel>
                            <Controller
                                name="categoriesIds"
                                id="categoriesIds"
                                control={control}
                                render={({field}) => (
                                    <Select
                                        labelId="categories-label" {...field}
                                        multiple
                                        onChange={e => {
                                            field.onChange(e);
                                            handleCategoriesChange(e);
                                        }}
                                        label="Catégories"
                                        renderValue={selected => (
                                            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                                {selected.map(categoryId => (
                                                    <Chip key={categoryId} label={getArchiveCategory(categoryId).name} />
                                                ))}
                                            </Box>
                                        )}
                                        error={!!errors.categoriesIds}
                                    >
                                        {archiveCategories.map(category => (
                                            <MenuItem key={category.id} value={category.id}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                        <Typography variant="inherit" color="textSecondary">{errors.nomineesIds?.message}</Typography>

                        {/* Récompenses */}
                        {fields.map((item, index) => (
                            <Box key={item.id}>
                                <h4>Vainqueur de la catégorie {item.categoryName}</h4>
                                <FormControl sx={{mt: 1}} fullWidth>
                                    <InputLabel id={'winners-label-' + item.id} error={!!errors.archiveAwards?.[index]?.winnersIds}>Vainqueurs</InputLabel>
                                    <Controller
                                        name={`archiveAwards.${index}.winnersIds`}
                                        id={'winners-' + item.id}
                                        control={control}
                                        render={({field, fieldState: {invalid, error}}) => (
                                            <Select
                                                labelId={'winners-label-' + item.id} {...field}
                                                multiple
                                                onChange={e => {
                                                    field.onChange(e);
                                                    handleWinnersChange(e);
                                                }}
                                                label="Vainqueurs"
                                                renderValue={selected => (
                                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                                        {selected.map(winnerId => (
                                                            <Chip key={winnerId} label={getArchivePerson(winnerId).firstName} />
                                                        ))}
                                                    </Box>
                                                )}
                                                error={invalid}
                                            >
                                                {archivePeople.map(person => (
                                                    <MenuItem key={person.id} value={person.id}>
                                                        {person.firstName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                <Typography variant="inherit" color="textSecondary">{errors?.archiveAwards?.[index]?.winnersIds?.message}</Typography>

                                <TextField
                                    margin="dense"
                                    id={'reason-' + item.id}
                                    name={'reason-' + item.id}
                                    label="Raison"
                                    fullWidth
                                    variant="standard"
                                    {...register(`archiveAwards.${index}.reason`)}
                                />
                            </Box>
                        ))}

                        {/*Losers de l'année*/}
                        {nbAwardsByPerson && (
                            <Box>
                                <h4>Loser(s) de l'année</h4>
                                <FormControl sx={{mt: 1}} fullWidth>
                                    <InputLabel id="losers-label">Loser(s) de l'année</InputLabel>
                                    <Controller
                                        name="losersIds"
                                        id="losersIds"
                                        control={control}
                                        render={({field}) => (
                                            <Select
                                                labelId="losers-label" {...field}
                                                multiple
                                                label="Loser(s) de l'année"
                                                renderValue={selected => (
                                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                                        {selected.map(loserId => (
                                                            <Chip key={loserId} label={getArchivePerson(loserId).firstName} />
                                                        ))}
                                                    </Box>
                                                )}
                                                error={!!errors.losersIds}
                                            >
                                                {archivePeople.filter(archivePerson => nbAwardsByPerson.get(archivePerson.id) === maxNbAwards).map(archivePerson => (
                                                    <MenuItem key={archivePerson.id} value={archivePerson.id}>
                                                        {archivePerson.firstName} ({nbAwardsByPerson.get(archivePerson.id)} récompenses)
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                <Typography variant="inherit" color="textSecondary">{errors.losersIds?.message}</Typography>
                            </Box>
                        )}
                        {/*{nbAwardsByPerson && [...nbAwardsByPerson.entries()].map(([key, value]) =>*/}
                        {/*    <Box key={key}>*/}
                        {/*        {getArchivePerson(key).firstName} : {value}*/}
                        {/*    </Box>*/}
                        {/*)}*/}

                        <Box>
                            <h4>Rapport de mi-saison</h4>
                            <TextField
                                margin="dense"
                                id="mid-report"
                                name="midReport"
                                label="Rapport de mi-saison"
                                fullWidth
                                multiline
                                maxRows={Infinity}
                                variant="standard"
                                {...register("midReport")}
                            />
                        </Box>

                        <Box>
                            <h4>Rapport</h4>
                            <TextField
                                margin="dense"
                                id="report"
                                name="report"
                                label="Rapport de mi-saison"
                                fullWidth
                                multiline
                                maxRows={Infinity}
                                variant="standard"
                                {...register("report")}
                            />
                        </Box>
                    </form>
                )}
            </DialogContent>
            <DialogActions>
                <Button type="submit" form="archivePersonForm">OK</Button>
                <Button onClick={() => onClose(false)}>Annuler</Button>
            </DialogActions>
        </Dialog>
    )
}
